import { Fragment } from "react";
import { Link } from "react-router-dom";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import CreateIcon from "@mui/icons-material/Create";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { TableCellProps } from "@mui/material/TableCell";
import { naWrapper } from "./utils";

const affiliatedSchoolKeys = [
  "depauw",
  "grinnell",
  "union",
  "knox",
  "lakeForest",
  "earlham",
  "ohioWesleyan",
  "wooster",
  "lewisClark",
  "barnard",
  "brynmawr",
  "mountHolyoke",
  "smith",
  "vassar",
  "wellesley",
] as const;
export type AffiliatedSchoolKey = (typeof affiliatedSchoolKeys)[number];

export const affiliatedSchools: {
  [key in AffiliatedSchoolKey]: { name: string; symbol: string };
} = {
  // carleton: { name: 'Carleton College', symbol: 'C' },
  barnard: { name: "Barnard College", symbol: "BN" },
  brynmawr: { name: "Bryn Mawr College", symbol: "BM" },
  depauw: { name: "DePauw University", symbol: "D" },
  earlham: { name: "Earlham College", symbol: "E" },
  grinnell: { name: "Grinnell College", symbol: "G" },
  knox: { name: "Knox College", symbol: "K" },
  lakeForest: { name: "Lake Forest College", symbol: "LF" },
  lewisClark: { name: "Lewis & Clark College", symbol: "LC" },
  mountHolyoke: { name: "Mount Holyoke College", symbol: "MH" },
  smith: { name: "Smith College", symbol: "S" },
  union: { name: "Union College", symbol: "U" },
  ohioWesleyan: { name: "Ohio Wesleyan University", symbol: "OW" },
  vassar: { name: "Vassar College", symbol: "V" },
  wellesley: { name: "Wellesley College", symbol: "WL" },
  wooster: { name: "College of Wooster", symbol: "W" },
};

export type AffiliatedSchoolChoice = {
  [key in AffiliatedSchoolKey]: boolean;
};

export interface SchoolChoice extends AffiliatedSchoolChoice {
  others: string;
}

export const filesToBeUploaded = ["推薦状１", "推薦状２", "留学中の調査書", "調査書"] as const;

export interface ApplicantInfo {
  id: string;
  type: string;
  applicantNum: string;
  orderedNum: string;
  name: string;
  englishName: string;
  kana: string;
  email: string;
  sex: string;
  dob: string;
  isComplete: string;
  highSchool: string;
  schoolCourse: string;
  schoolDepartment: string;
  license: string;
  schoolChoice: SchoolChoice;
  churyoku: string;
  yakuin: string;
  club: string;
  strength: string;
  selfPR: string;
  paper1: string;
  abroad: string;
  familyIncome: string;
  nationality: string;
  isRecDelivered: string;
  tags: string[];
  result1: string;
  s3keys?: string[];
  missingFiles: (typeof filesToBeUploaded)[number][];
  recommendation1: string;
  recommendation2: string;
  recommendation3: string;
  schoolReport: string;
  background: string;
  family: string;
  specialReason: string;
  guardianContact: string;
  firstChoice: string;
  secondChoice: string;
  sutematsu: string;
  top3schoolsReasons: string;
  intendedMajor: string;
  englishTestScore: string;
  otherTestScores: string;
  recommender1Name: string;
  recommender2Name: string;
  recommender3Name: string;
  schoolReportWriterName: string;
  recommender1Email: string;
  recommender2Email: string;
  recommender3Email: string;
  schoolReportWriterEmail: string;
  recommendationCheck: string;
  oath1: string;
  oath2: string;
  oath3: string;
  oath4: string;
  // Syutsugan record-creation date ("YYYY/MM/DD HH:MM:SS") = when the applicant
  // started; the dashboard over-time chart's x-axis. Set by the ingestion
  // Lambda (from syutsugan `created_at`).
  applicationDate?: string;
  // Amplify-managed timestamp (ISO). Not set on ingestion-Lambda PutItem rows,
  // so NOT used for the timeline; kept for completeness.
  createdAt?: string;
}

export const searchableFields: (keyof ApplicantInfo)[] = [
  "name",
  "kana",
  "highSchool",
  "churyoku",
  "schoolChoice",
  "yakuin",
  "club",
  "strength",
  "selfPR",
  "paper1",
];

export const columnKeys = [
  "id",
  "type",
  "applicantNum",
  "orderedNum",
  "isComplete",
  "name",
  // "imageUrl",
  "highSchool",
  // "schoolChoice",
  "top3schoolsReasons",
  "sutematsu",
  ...affiliatedSchoolKeys,
  // removed missingFiles from column keys after adding recommendation1, recommendation2, schoolReport
  // "missingFiles",
  "recommendation1",
  "recommendation2",
  "recommendation3",
  "schoolReport",
  "tags",
  "result1",
  "firstChoice",
  "secondChoice",
  // "scholarshipFirst",
  // "scholarshipSecond",
] as const;

export type ColumnKey = (typeof columnKeys)[number];

export interface ColDef extends TableCellProps {
  title?: string;
  headerOnClick?: "sort" | "filter";
  width?: string | number;
  renderCell?: (
    value: any,
    id: string,
    filters: Filters,
    setFilters: React.Dispatch<React.SetStateAction<Filters>>
  ) => JSX.Element;
}

const ViewButton: React.FC<{ id: string }> = ({ id }) => {
  return (
    <a
      href={`https://www.syutsugan.net/applicants/${id}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      出願ネット
    </a>
  );
};

const clippedText = (value: string, length: number) => {
  let content: string;
  if (naWrapper(value).length === 0) content = "";
  else if (value.length > length) content = `${value.slice(0, length)}......`;
  else content = value;
  return (
    <Tooltip
      title={<Typography>{naWrapper(value)}</Typography>}
      disableHoverListener={value.length <= length}
    >
      <div
      // style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
      >
        {content}
      </div>
    </Tooltip>
  );
};

const scholarshipRenderCell = (value: string, bold = false) => (
  <div style={{ fontWeight: bold ? "bold" : undefined }}>
    {value.slice(0, 4)}
  </div>
);

export const columns: { [key in ColumnKey]?: ColDef } = {
  isComplete: {
    align: "center",
    title: "出願済",
    headerOnClick: "sort",
    width: "5rem",
    padding: "none",
    renderCell: (value: string) =>
      naWrapper(value).includes("領収") ? (
        <CheckIcon fontSize="small" color="primary" />
      ) : (
        <CreateIcon fontSize="small" color="warning" />
      ),
  },
  result1: {
    align: "center",
    title: "書類合否",
    headerOnClick: "sort",
    width: "6rem",
    padding: "none",
    // align: 'center',
    renderCell: (value?: string) => {
      if (value?.includes("不合格"))
        return <Typography color="error">不合格</Typography>;
      else if (value?.includes("合格"))
        return (
          <Typography color="primary" fontWeight="bold">
            合格
          </Typography>
        );
      else return <div />;
    },
  },
  id: {
    align: "center",
    width: "5rem",
    padding: "none",
    renderCell: (value: string, id: string) => <ViewButton id={id} />,
  },
  // Submitted files are tracked in DataContextProvider (sets value for each applicant)
  recommendation1: {
    align: "center",
    title: "推薦状１",
    headerOnClick: "filter",
    width: "7rem",
    padding: "none",
    renderCell: (value: string) => {
      if (value === "推薦状１")
        return <CheckIcon fontSize="small" color="primary" />;
      else return <WarningAmberIcon fontSize="small" color="error" />;
    },
  },
  recommendation2: {
    align: "center",
    title: "推薦状２",
    headerOnClick: "filter",
    width: "7rem",
    padding: "none",
    renderCell: (value: string) => {
      if (value === "推薦状２")
        return <CheckIcon fontSize="small" color="primary" />;
      else return <WarningAmberIcon fontSize="small" color="error" />;
    },
  },
  schoolReport: {
    align: "center",
    title: "調査書",
    headerOnClick: "filter",
    width: "7rem",
    padding: "none",
    renderCell: (value: string) => {
      if (value === "調査書")
        return <CheckIcon fontSize="small" color="primary" />;
      else return <WarningAmberIcon fontSize="small" color="error" />;
    },
  },
  recommendation3: {
    align: "center",
    title: "留学中の調査書",
    headerOnClick: "filter",
    width: "9rem",
    padding: "none",
    renderCell: (value: string) => {
      if (value === "留学中の調査書")
        return <CheckIcon fontSize="small" color="primary" />;
      else return <WarningAmberIcon fontSize="small" color="error" />;
    },
  },
  firstChoice: {
    align: "center",
    title: "第1志望",
    headerOnClick: "sort",
    width: "7rem",
    padding: "none",
    renderCell: (value: string) => scholarshipRenderCell(value, true),
  },
  secondChoice: {
    align: "center",
    title: "第2志望",
    headerOnClick: "sort",
    width: "7rem",
    padding: "none",
    renderCell: (value: string) => scholarshipRenderCell(value),
  },
  name: {
    title: "氏名",
    headerOnClick: "sort",
    width: "13rem",
    renderCell: (value: string, id: string) => (
      <Link to={`/${id}`} style={{ textDecoration: "none" }}>
        <Typography variant="h6" style={{ fontSize: "1.1rem" }}>
          {value}
        </Typography>
      </Link>
    ),
  },
  highSchool: {
    title: "高校名",
    headerOnClick: "sort",
    width: "18rem",
  },
  top3schoolsReasons: {
    title: "志望校",
    headerOnClick: "sort",
    width: "18rem",
    renderCell: (value: string) => clippedText(value, 60),
  },

  ...(affiliatedSchoolKeys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: {
        title: affiliatedSchools[key].symbol,
        headerOnClick: "filter",
        width: "2.5rem",
        align: "center",
        padding: "none",
        renderCell: (value: boolean, id: string) =>
          value ? <CheckIcon fontSize="small" /> : <Fragment />,
      },
    }),
    {}
  ) as { [key in AffiliatedSchoolKey]: ColDef }),
  tags: {
    title: "Tags",
    width: "15rem",
    renderCell: (tags: string[], id: string, filters, setFilters) => {
      const targetTags = filters.tags as string[] | null | undefined;
      return (
        <Fragment>
          {tags.map((tag) => (
            <Chip
              key={`tag-${tag}`}
              label={tag}
              style={{ margin: ".25rem" }}
              color={
                targetTags && targetTags.includes(tag) ? "primary" : "default"
              }
              onClick={() => {
                if (targetTags?.includes(tag)) {
                  const newTargetTags = targetTags.filter((t) => t !== tag);
                  setFilters({
                    ...filters,
                    tags: newTargetTags.length === 0 ? null : newTargetTags,
                  });
                } else
                  setFilters({
                    ...filters,
                    tags: [...(targetTags ?? []), tag],
                  });
              }}
            />
          ))}
        </Fragment>
      );
    },
  },
};

export type Filters = {
  [key in ColumnKey]?: string[] | string | boolean | null | "submitted" | "unsubmitted";
};

export type RecommendationFileType = keyof Recommendee["documentsUploaded"];

export type RecommendationTarget = {
  recommendee: Recommendee;
  fileType: RecommendationFileType;
};

export type RecommendationDocumentAssigned = {
  assignee: string | null;
  fileUrl: string | null;
};

export type Recommendee = {
  // id: string;
  applicantNum: string;
  // lastName: string;
  // firstName: string;
  name: string;
  // lastNameEn: string;
  // firstNameEn: string;
  englishName: string;
  imageUrl: string;
  documentsUploaded: {
    report?: RecommendationDocumentAssigned;
    rec1?: RecommendationDocumentAssigned;
    rec2?: RecommendationDocumentAssigned;
    rec3?: RecommendationDocumentAssigned;
  };
};

export type Lang = "ja" | "en";
